import products from "../../../shared-protos/catalogs/software-inventory-products.json";
import { runQuery, rowsToObjects, rangeToTimeRange } from "./analyticsClient.js";
import { toApiRange } from "./shell.js";
import { ensureQueryJoins } from "./queryLangJoins.js";
import { parseServerTimestamp } from "./traceUtils.js";

export const FLEET_PAGE_SIZE = 30;
export const FLEET_SOURCES = ["inventory", "executions", "runtimes", "models"];
export const FLEET_SOURCE_LABELS = {
    inventory: "Latest reported inventory",
    executions: "Observed AI application execution",
    runtimes: "Runtime and module observations",
    models: "Models in captured activity",
};
export const FLEET_KINDS = { application: "AI Agent / application", runtime: "Runtime", model: "Model", unknown: "Unknown type" };
const catalog = products.map(({ legacy_key, product }) => ({ ...product, legacy_key }));
const namesFor = (items) => items.flatMap((p) => [p.display_name, p.legacy_key]);
const runtimeNames = namesFor(catalog.filter((p) => p.category === "local_runtime"));
const applicationNames = namesFor(catalog.filter((p) => p.category !== "local_runtime"));
const knownNames = [...runtimeNames, ...applicationNames];
const ZERO = "00000000-0000-0000-0000-000000000000";
const moduleLabels = { "onnxruntime.dll": "ONNX Runtime", "libonnxruntime.so": "ONNX Runtime", "libonnxruntime.dylib": "ONNX Runtime", "torch_cpu.dll": "PyTorch", "libtorch_cpu.so": "PyTorch", "libtorch_cpu.dylib": "PyTorch", "tensorflow.dll": "TensorFlow", "libtensorflow.so": "TensorFlow", "libtensorflow.dylib": "TensorFlow", "llama.dll": "llama.cpp", "libllama.so": "llama.cpp", "libllama.dylib": "llama.cpp" };
const runtimeModules = ["onnxruntime.dll", "libonnxruntime.so", "libonnxruntime.dylib", "torch_cpu.dll", "libtorch_cpu.so", "libtorch_cpu.dylib", "tensorflow.dll", "libtensorflow.so", "libtensorflow.dylib", "llama.dll", "libllama.so", "libllama.dylib"];
const f = (alias, entity, field) => ({ stepAlias: alias, field: `DIMENSION_FIELD_${entity}_${field}` });
const dim = (field, outputAlias) => ({ field, outputAlias });
const pred = (field, op, value) => ({ pred: { field, op: `OPERATOR_${op}`, value } });
const and = (...children) => ({ andExpr: { children: children.filter(Boolean) } });
const or = (...children) => ({ orExpr: { children: children.filter(Boolean) } });
const metric = (field, op, outputAlias, predicate = null) => ({ outputAlias, computed: { field, op: `AGGREGATE_OP_${op}`, ...(predicate ? { predicate } : {}) } });
const nonempty = (field) => pred(field, "NE", ZERO);
const contains = (field, text) => pred(field, "ILIKE", `%${String(text).replace(/[\\%_]/g, "\\$&")}%`);
const latest = (value) => { const ms = value ? parseServerTimestamp(value) : NaN; return Number.isFinite(ms) && ms > 0 ? new Date(ms).toISOString() : null; };

export function inventoryIdentity(name) {
    // Only the frozen compatibility registry can establish a product alias.
    // Arbitrary names from different sources never establish shared identity.
    return catalog.find((p) => p.display_name === name || p.legacy_key === name) || null;
}
export function fleetRecord(source, row) {
    const raw = String(row.name ?? "");
    const product = source === "inventory" ? inventoryIdentity(raw) : null;
    const moduleLoads = Number(row.moduleLoads) || 0;
    const gpuInferences = Number(row.gpuInferences) || 0;
    const kind = source === "models" ? "model" : source === "runtimes" ? (runtimeModules.includes(raw) ? "runtime" : "unknown") : source === "executions" ? "application" : product ? (product.category === "local_runtime" ? "runtime" : "application") : "unknown";
    return {
        id: `${source}:${raw}`, source, key: raw, productId: product?.product_id || null,
        name: product?.display_name || (source === "runtimes" && moduleLabels[raw] ? `${moduleLabels[raw]} (${raw})` : raw) || (source === "models" ? "Unreported model" : "Unreported name"), kind,
        evidence: source === "inventory" ? "Presence reported" : source === "runtimes" ? gpuInferences > 0 ? moduleLoads > 0 ? "Module load and GPU inference" : "GPU inference observed" : moduleLoads > 0 ? "Module load observed" : "Runtime evidence unresolved" : "Observed use",
        location: source === "inventory" || source === "executions" || source === "runtimes" ? "endpoint" : "unknown",
        endpoints: row.endpoints === null ? null : Number(row.endpoints) || 0, cloudAssociations: Number(row.cloudAssociations) || 0, withoutEndpoint: Number(row.withoutEndpoint) || 0,
        observations: Number(row.observations) || 0, moduleLoads, gpuInferences,
        hasUnknownAssociation: Boolean(Number(row.unknownAssociation) || Number(row.withoutEndpoint)),
        inventoryAt: source === "inventory" ? latest(row.latest) : null,
        useAt: source === "inventory" ? null : latest(row.latest),
    };
}

export function readFleetRoute(params) {
    const oneOf = (key, allowed, fallback = "") => allowed.includes(params.get(key)) ? params.get(key) : fallback;
    return {
        view: oneOf("fleetView", ["ai", "endpoints"], "ai"),
        q: (params.get("fleetSearch") || "").slice(0, 200),
        group: (params.get("fleetGroup") || "").slice(0, 200),
        type: oneOf("fleetType", Object.keys(FLEET_KINDS)),
        location: oneOf("fleetLocation", ["endpoint", "cloud", "unknown"]),
        evidence: oneOf("fleetEvidence", ["presence", "use"]),
        selected: (params.get("fleetSelected") || "").slice(0, 500),
    };
}
export function writeFleetRoute(params, changes) {
    const next = new URLSearchParams(params);
    const keys = { view: "fleetView", q: "fleetSearch", type: "fleetType", location: "fleetLocation", evidence: "fleetEvidence", selected: "fleetSelected", group: "fleetGroup" };
    for (const [key, value] of Object.entries(changes)) {
        if (!keys[key]) continue;
        if (!value || (key === "view" && value === "ai")) next.delete(keys[key]);
        else next.set(keys[key], value);
    }
    return next;
}

function sourceDefinition(source) {
    if (source === "inventory") return { entity: "AGENT_INVENTORY", alias: "i", name: "AGENT_NAME", endpoint: "ENDPOINT_ID", time: null, identity: "ENDPOINT_ID", location: "endpoint" };
    if (source === "executions") return { entity: "AI_AGENT_EXECUTION", alias: "x", name: "IMAGE_NAME", endpoint: "ENDPOINT_ID", time: "TIMESTAMP", identity: "PROCESS_START_KEY", location: "endpoint" };
    if (source === "runtimes") return { entity: "AI_SERVICE_OBSERVATION", alias: "o", name: "MODULE_NAME", endpoint: "ENDPOINT_ID", time: "OBSERVED_AT", identity: "EVENT_ID", location: "endpoint" };
    if (source === "models") return { entity: "PROMPT", alias: "p", name: "MODEL", endpoint: "ENDPOINT_ID", time: "EVENT_TIMESTAMP", identity: "LOGICAL_PROMPT_ID", location: "unknown" };
    throw new Error("Unknown Fleet source");
}

export function fleetSourceSpec(source, { filters = {}, range = "1M", offset = 0, summary = false, selected = null, associations = false, endpointId = null, endpointMetadata = true } = {}) {
    const def = sourceDefinition(source);
    const field = (key) => f(def.alias, def.entity, key);
    const name = field(def.name), ep = field(def.endpoint);
    if (filters.evidence === "presence" && source !== "inventory" && source !== "runtimes") return null;
    if (filters.evidence === "use" && source === "inventory") return null;
    if (filters.type === "model" && source !== "models") return null;
    if (filters.type && filters.type !== "model" && source === "models") return null;
    if (filters.type && filters.type !== "application" && source === "executions") return null;
    if (filters.type === "application" && source === "runtimes") return null;
    if (filters.location && filters.location !== "endpoint" && def.location === "endpoint") return null;
    let root = { alias: def.alias, entity: `ENTITY_TYPE_${def.entity}` };
    const predicates = [];
    if (endpointId) predicates.push(pred(ep, "EQ", endpointId));
    if (source === "inventory") {
        root.children = [{ edge: "EDGE_TYPE_AGENT_INVENTORY_ON_ENDPOINT", mode: "JOIN_MODE_INNER", target: { alias: "e", entity: "ENTITY_TYPE_ENDPOINT" } }];
        predicates.push(pred(f("e", "ENDPOINT", "IS_SYNTHETIC"), "EQ", false));
        if (filters.type) predicates.push(pred(name, filters.type === "unknown" ? "NOT_IN" : "IN", filters.type === "runtime" ? runtimeNames : filters.type === "application" ? applicationNames : knownNames));
    }
    if (source === "runtimes") {
        predicates.push(pred(field("EVIDENCE_KIND"), "IN", filters.evidence === "presence" ? ["module_load"] : filters.evidence === "use" ? ["gpu_inference"] : ["module_load", "gpu_inference"]));
        if (filters.type) predicates.push(pred(name, filters.type === "runtime" ? "IN" : "NOT_IN", runtimeModules));
    }
    if (source === "models" && endpointMetadata) {
        root.children = [{ edge: "EDGE_TYPE_PROMPT_ON_ENDPOINT", mode: "JOIN_MODE_LEFT", target: { alias: "e", entity: "ENTITY_TYPE_ENDPOINT" } }];
        if (filters.location) predicates.push(filters.location === "cloud" ? pred(f("e", "ENDPOINT", "IS_SYNTHETIC"), "EQ", true)
            : filters.location === "endpoint" ? and(nonempty(ep), nonempty(f("e", "ENDPOINT", "ENDPOINT_ID")), pred(f("e", "ENDPOINT", "IS_SYNTHETIC"), "EQ", false))
                : or(pred(ep, "EQ", ZERO), pred(f("e", "ENDPOINT", "ENDPOINT_ID"), "EQ", ZERO)));
        if (associations) predicates.push(nonempty(f("e", "ENDPOINT", "ENDPOINT_ID")), pred(f("e", "ENDPOINT", "IS_SYNTHETIC"), "EQ", false));
    }
    if (source === "models" && !endpointMetadata && filters.location) throw new Error("Endpoint metadata access is required to classify execution location.");
    if (source === "models" && filters.group) {
        root = ensureQueryJoins(root, ["identity"]);
        predicates.push(pred(f("du", "DIRECTORY_USER", "DEPARTMENT"), "EQ", filters.group));
    }
    if (filters.q?.trim()) {
        const q = filters.q.trim();
        const aliases = source === "inventory" ? namesFor(catalog.filter((p) => p.display_name.toLowerCase().includes(q.toLowerCase())))
            : source === "runtimes" ? Object.entries(moduleLabels).filter(([, label]) => label.toLowerCase().includes(q.toLowerCase())).map(([module]) => module) : [];
        predicates.push(aliases.length ? or(contains(name, q), pred(name, "IN", aliases)) : contains(name, q));
    }
    if (selected !== null) predicates.push(pred(name, "EQ", selected));
    const timeField = source === "inventory" ? f("e", "ENDPOINT", "LAST_UPDATED") : field(def.time);
    const realEndpoint = source === "models" && endpointMetadata ? f("e", "ENDPOINT", "ENDPOINT_ID") : ep;
    const endpointCondition = source === "models" && endpointMetadata ? and(nonempty(realEndpoint), pred(f("e", "ENDPOINT", "IS_SYNTHETIC"), "EQ", false)) : nonempty(ep);
    const metrics = [
        metric(realEndpoint, "COUNT_DISTINCT", "endpoints", endpointCondition),
        metric(field(def.identity), "COUNT_DISTINCT", "observations"),
        metric(field(def.identity), "COUNT_DISTINCT", "withoutEndpoint", pred(ep, "EQ", ZERO)),
        metric(timeField, "MAX", "latest"),
    ];
    if (source === "runtimes") {
        metrics.push(metric(field("EVENT_ID"), "COUNT_DISTINCT", "moduleLoads", pred(field("EVIDENCE_KIND"), "EQ", "module_load")),
            metric(field("EVENT_ID"), "COUNT_DISTINCT", "gpuInferences", pred(field("EVIDENCE_KIND"), "EQ", "gpu_inference")));
    }
    if (source === "models" && endpointMetadata) {
        metrics.push(metric(realEndpoint, "COUNT_DISTINCT", "cloudAssociations", pred(f("e", "ENDPOINT", "IS_SYNTHETIC"), "EQ", true)),
            // A same-alias marker includes orphaned references as well as zero IDs.
            // It is not a logical-prompt count and must not be summed into totals.
            metric(realEndpoint, "COUNT_DISTINCT", "unknownAssociation", pred(realEndpoint, "EQ", ZERO)));
    }
    if (summary) {
        metrics.push(metric(name, "COUNT_DISTINCT", "total"));
        if (source === "inventory") {
            for (const [kind, names] of [["application", applicationNames], ["runtime", runtimeNames], ["unknown", knownNames]]) {
                const condition = pred(name, kind === "unknown" ? "NOT_IN" : "IN", names);
                metrics.push(metric(name, "COUNT_DISTINCT", `${kind}Types`, condition));
                metrics.push(metric(ep, "COUNT", `${kind}Presence`, condition));
            }
        }
    }
    const dimensions = summary ? [] : [dim(associations ? ep : name, associations ? "id" : "name")];
    return {
        root,
        timeRange: rangeToTimeRange(toApiRange(range)), // The current inventory entity has no time field; the server ignores this window for its snapshot.
        ...(predicates.length ? { filter: and(...predicates) } : {}),
        output: { dimensions, metrics },
        ...(!summary ? { limit: FLEET_PAGE_SIZE, offset: Math.max(0, Math.trunc(Number(offset) || 0)), orderBy: [{ outputAlias: associations ? "id" : "name", direction: "DIRECTION_ASC" }] } : {}),
    };
}

export async function readFleetSource(args, execute = runQuery) {
    const pageSpec = fleetSourceSpec(args.source, args);
    if (!pageSpec) return { rows: [], summary: null, excluded: true, partial: false };
    const [page, summary] = await Promise.all([
        execute(pageSpec), execute(fleetSourceSpec(args.source, { ...args, summary: true })),
    ]);
    return {
        rows: rowsToObjects(page).map((row) => fleetRecord(args.source, args.source === "models" && args.endpointMetadata === false ? { ...row, endpoints: null } : row)),
        summary: rowsToObjects(summary)[0] || {},
        partial: Boolean(page.truncatedBySize || page.truncated_by_size || summary.truncatedBySize || summary.truncated_by_size),
        excluded: false,
    };
}
export async function readFleetAssociations(args, execute = runQuery) {
    const spec = fleetSourceSpec(args.source, { ...args, associations: true });
    if (!spec) return { rows: [], partial: false };
    const result = await execute(spec);
    return { rows: rowsToObjects(result), partial: Boolean(result.truncatedBySize || result.truncated_by_size) };
}

export async function readFleetEndpointUse({ endpointIds = [], range = "1M" }, execute = runQuery) {
    if (endpointIds.length > 100) throw new Error("Fleet endpoint activity requires a bounded page of at most 100 endpoints.");
    const ids = [...new Set(endpointIds)];
    if (!ids.length) return { rows: [], endpointIds: [], partial: false };
    const ep = f("p", "PROMPT", "ENDPOINT_ID");
    const result = await execute({
        root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
        timeRange: rangeToTimeRange(toApiRange(range)),
        filter: pred(ep, "IN", ids),
        output: { dimensions: [dim(ep, "id")], metrics: [metric(f("p", "PROMPT", "LOGICAL_PROMPT_ID"), "COUNT_DISTINCT", "observations"), metric(f("p", "PROMPT", "EVENT_TIMESTAMP"), "MAX", "latest")] },
        limit: ids.length,
    });
    return { rows: rowsToObjects(result), endpointIds: ids, partial: Boolean(result.truncatedBySize || result.truncated_by_size) };
}

export async function readFleetPerson({ id }, execute = runQuery) {
    const ref = (key) => f("du", "DIRECTORY_USER", key);
    return rowsToObjects(await execute({
        root: { alias: "du", entity: "ENTITY_TYPE_DIRECTORY_USER" },
        timeRange: { relative: { durationSeconds: "7776000" } },
        filter: pred(ref("SOURCE_ID"), "EQ", id),
        output: { dimensions: [dim(ref("SOURCE_ID"), "id"), dim(ref("DISPLAY_NAME"), "name"), dim(ref("EMAIL"), "email"), dim(ref("DEPARTMENT"), "department")] },
        limit: 1,
    }))[0] || null;
}
